// frontend/src/components/FilterPanel.js
import React, { useState } from 'react';
import './FilterPanel.css';

const FilterPanel = ({ currentFilters, onApply, onClose }) => {
  const [filters, setFilters] = useState(currentFilters);

  // Topics organized by grade - FIXED: Case must match database exactly!
  const topics = {
    8: [
      'Whole Numbers', 'Integers', 'Exponents', 'Common Fractions', 'Decimal Fractions',
      'Percentages', 'Ratio and Rate', 'Finance', 'Algebraic Expressions', 'Algebraic Equations',
      'Numeric Patterns', 'Graphs', 'Geometry of 2D Shapes', 'Geometry of 3D Objects',
      'Transformation Geometry', 'Theorem of Pythagoras', 'Area and Perimeter',
      'Surface Area and Volume', 'Data Handling', 'Probability'
    ],
    9: [
      'Whole Numbers', 'Integers', 'Exponents', 'Common Fractions', 'Decimal Fractions',
      'Percentages', 'Financial Mathematics',
      'Numeric Patterns', 'Geometric Patterns', 'Functions and Relationships',
      'Algebraic Expressions', 'Algebraic Equations', 'Graphs',
      'Construction of Geometric Figures', 'Geometry of 2D Shapes',
      'Geometry of Straight Lines', 'Geometry of 3D Objects', 'Transformation Geometry',
      'Theorem of Pythagoras', 'Area and Perimeter', 'Surface Area and Volume',
      'Data Handling', 'Probability'
    ],
    10: [
      'Algebra', 'Number Patterns', 'Functions', 'Finance', 'Trigonometry',
      'Euclidean Geometry', 'Analytical Geometry', 'Statistics', 'Probability'
    ],
    11: [
      'Algebra', 'Functions', 'Finance', 'Trigonometry',
      'Euclidean Geometry', 'Analytical Geometry', 'Statistics',
      'Probability', 'Patterns and Sequences'
    ],
    12: [
      'Algebra', 'Functions', 'Finance', 'Trigonometry',
      'Euclidean Geometry', 'Analytical Geometry', 'Statistics',
      'Probability', 'Calculus', 'Patterns and Sequences'
    ]
  };

  // Subtopics organized by topic - FIXED: Keys must match topic names exactly
  const subtopics = {
    // Grade 8-9 subtopics
    'Integers': ['Addition', 'Subtraction', 'Multiplication', 'Division', 'Order of Operations'],
    'Exponents': ['Laws of Exponents', 'Scientific Notation', 'Negative Exponents', 'Zero Exponent'],
    'Common Fractions': ['Addition', 'Subtraction', 'Multiplication', 'Division', 'Mixed Numbers'],
    'Decimal Fractions': ['Operations', 'Rounding', 'Conversions'],
    'Percentages': ['Calculations', 'Increase', 'Decrease', 'Word Problems'],
    'Ratio and Rate': ['Simplifying Ratios', 'Rate Calculations', 'Proportion'],
    'Algebraic Expressions': ['Simplification', 'Substitution', 'Expansion', 'Factorization'],
    'Algebraic Equations': ['Linear Equations', 'Equations with Fractions', 'Word Problems'],
    'Numeric Patterns': ['Linear Patterns', 'General Term', 'Finding Formulas'],
    'Geometric Patterns': ['Visual Patterns', 'Number Patterns', 'Finding Rules'],
    'Graphs': ['Gradient', 'Y-intercept', 'Equation of Line', 'Plotting'],
    'Geometry of 2D Shapes': ['Triangles', 'Quadrilaterals', 'Circles', 'Angle Properties', 'Parallel Lines'],
    'Geometry of 3D Objects': ['Prisms', 'Pyramids', 'Cylinders', 'Cones', 'Spheres'],
    'Transformation Geometry': ['Reflection', 'Translation', 'Rotation', 'Enlargement'],
    'Theorem of Pythagoras': ['Finding Hypotenuse', 'Finding a Side', 'Applications'],
    'Area and Perimeter': ['Rectangle', 'Triangle', 'Circle', 'Trapezium', 'Composite Shapes'],
    'Surface Area and Volume': ['Rectangular Prism', 'Cylinder', 'Triangular Prism', 'Cone', 'Sphere'],
    'Data Handling': ['Mean', 'Median', 'Mode', 'Range', 'Graphs'],
    'Financial Mathematics': ['Simple Interest', 'Compound Interest', 'Hire Purchase', 'Exchange Rates'],
    'Whole Numbers': ['Real Number System', 'Prime Factorisation', 'LCM and HCF', 'Ratio and Rate', 'Direct and Indirect Proportion', 'Speed Distance and Time'],
    'Functions and Relationships': ['Input-Output', 'Flow Diagrams', 'Tables', 'Formulae', 'Equations', 'Graphs'],
    'Construction of Geometric Figures': ['Bisecting Lines', 'Bisecting Angles', 'Perpendicular Lines', 'Constructing Triangles', 'Constructing Quadrilaterals', 'Constructing Angles Without Protractor'],
    'Geometry of Straight Lines': ['Angles on a Straight Line', 'Vertically Opposite Angles', 'Corresponding Angles', 'Alternate Angles', 'Co-interior Angles'],
    'Area and Perimeter': ['Triangles', 'Rectangles and Squares', 'Circles', 'Composite Shapes', 'Unit Conversions'],
    // Grade 10-12 subtopics
    'Algebra': ['Equations', 'Inequalities', 'Polynomials', 'Sequences', 'Surds', 'Exponents'],
    'Number Patterns': ['Linear Patterns', 'Quadratic Patterns', 'General Term'],
    'Functions': ['Linear', 'Quadratic', 'Exponential', 'Hyperbolic', 'Inverse Functions'],
    'Finance': ['Simple Interest', 'Compound Interest', 'Annuities', 'Loans', 'Investments'],
    'Trigonometry': ['Ratios', 'Identities', 'Equations', 'Graphs', '2D Problems', '3D Problems'],
    'Euclidean Geometry': ['Circle Theorems', 'Triangles', 'Quadrilaterals', 'Similarity', 'Proportionality'],
    'Analytical Geometry': ['Distance', 'Midpoint', 'Gradient', 'Straight Line', 'Circles'],
    'Statistics': ['Data Analysis', 'Measures of Central Tendency', 'Regression', 'Correlation'],
    'Probability': ['Counting Principles', 'Probability Rules', 'Venn Diagrams', 'Tree Diagrams'],
    'Calculus': ['Limits', 'Differentiation', 'Applications', 'Optimization', 'Rates of Change'],
    'Patterns and Sequences': ['Arithmetic', 'Geometric', 'Sigma Notation', 'Convergence']
  };

  const handleGradeChange = (grade) => {
    const newGrade = parseInt(grade);
    const availableTopics = topics[newGrade] || [];
    setFilters(prev => ({
      ...prev,
      grade: newGrade,
      // Check if current topic exists in new grade (case-sensitive)
      topic: availableTopics.includes(prev.topic) ? prev.topic : availableTopics[0] || '',
      subtopic: ''
    }));
  };

  const handleTopicChange = (topic) => {
    setFilters(prev => ({
      ...prev,
      topic,
      subtopic: ''
    }));
  };

  const handleApply = () => {
    onApply(filters);
  };

  // Get current available topics based on selected grade
  const availableTopics = topics[filters.grade] || topics[10];
  const availableSubtopics = subtopics[filters.topic] || [];

  return (
    <div className="filter-panel-overlay">
      <div className="filter-panel">
        <div className="filter-panel-header">
          <h3>Study Focus Filters</h3>
          <button className="close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="filter-panel-body">
          <div className="filter-group">
            <label>Grade</label>
            <div className="grade-selector">
              {[8, 9, 10, 11, 12].map(grade => (
                <button
                  key={grade}
                  className={`grade-btn ${filters.grade === grade ? 'active' : ''}`}
                  onClick={() => handleGradeChange(grade)}
                >
                  Grade {grade}
                </button>
              ))}
            </div>
          </div>

          <div className="filter-group">
            <label>Topic</label>
            <select
              value={filters.topic}
              onChange={(e) => handleTopicChange(e.target.value)}
              className="filter-select"
            >
              {availableTopics.map(topic => (
                <option key={topic} value={topic}>
                  {topic}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Subtopic (Optional)</label>
            <select
              value={filters.subtopic}
              onChange={(e) => setFilters(prev => ({ ...prev, subtopic: e.target.value }))}
              className="filter-select"
            >
              <option value="">All Subtopics</option>
              {availableSubtopics.map(subtopic => (
                <option key={subtopic} value={subtopic}>
                  {subtopic}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="filter-panel-footer">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={handleApply}>
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;
